"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

interface FooterLinkProps {
  href: string;
  label: string;
}

// Define translations
const translations = {
  en: {
    subscribe: "Subscribe",
    emailPlaceholder: "johndoe@email.com",
    newsletterText: "Subscribe to our newsletter for the latest updates, exclusive recipes, special offers, and behind-the-scenes stories from our chocolate factory.",
    invalidEmail: "Please enter a valid email address.",
    alreadySubscribed: "This email is already subscribed.",
    tooManyRequests: "Too many requests. Please try again later.",
    subscriptionSuccess: "Subscription successful!",
    errorMessage: "An error occurred. Please try again.",
    navigation: {
      home: "Home",
      products: "Products",
      forChefs: "For Chefs",
      contact: "Contact Us",
      about: "About Us"
    },
    legal: {
      terms: "TERMS",
      privacy: "PRIVACY",
      cookies: "COOKIES"
    },
    copyright: "© Yurorika Botech Gida 2025"
  },
  tr: {
    subscribe: "Abone Ol",
    emailPlaceholder: "ornek@email.com",
    newsletterText: "En son güncellemeler, özel tarifler, özel teklifler ve çikolata fabrikamızdan perde arkası hikayeleri için bültenimize abone olun.",
    invalidEmail: "Lütfen geçerli bir e-posta adresi girin.",
    alreadySubscribed: "Bu e-posta zaten abonedir.",
    tooManyRequests: "Çok fazla istek. Lütfen daha sonra tekrar deneyin.",
    subscriptionSuccess: "Abonelik başarılı!",
    errorMessage: "Bir hata oluştu. Lütfen tekrar deneyin.",
    navigation: {
      home: "Ana Sayfa",
      products: "Ürünler",
      forChefs: "Şefler İçin",
      contact: "İletişim",
      about: "Hakkımızda"
    },
    legal: {
      terms: "ŞARTLAR",
      privacy: "GİZLİLİK",
      cookies: "ÇEREZLER"
    },
    copyright: "© Yurorika Botech Gıda 2025"
  }
};

export default function Footer() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [language, setLanguage] = useState<"en" | "tr">("en");

  // Listen for language changes and initialize
  useEffect(() => {
    // Function to get the current language
    const getCurrentLanguage = () => {
      try {
        const storedLanguage = localStorage.getItem("selectedLanguage");
        return (storedLanguage === "en" || storedLanguage === "tr") ? storedLanguage as "en" | "tr" : "en";
      } catch (error) {
        console.error("Error accessing localStorage:", error);
        return "en";
      }
    };

    // Set initial language
    setLanguage(getCurrentLanguage());

    // Handler for storage events (different tabs)
    const handleStorageChange = () => {
      setLanguage(getCurrentLanguage());
    };

    // Handler for custom events (same window)
    const handleLanguageChange = () => {
      setLanguage(getCurrentLanguage());
    };

    // Set up event listeners
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("languageChange", handleLanguageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("languageChange", handleLanguageChange);
    };
  }, []);

  const handleSubscribe = async () => {
    if (!email.includes("@")) {
      setMessage(translations[language].invalidEmail);
      setMessageType("error");
      return;
    }

    try {
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.status === 409) {
        setMessage(translations[language].alreadySubscribed);
        setMessageType("success");
        return;
      }

      if (response.status === 429) {
        setMessage(translations[language].tooManyRequests);
        setMessageType("error");
        return;
      }

      if (!response.ok) {
        setMessage(data.message || translations[language].errorMessage);
        setMessageType("error");
        return;
      }

      setMessage(translations[language].subscriptionSuccess);
      setMessageType("success");
      setEmail("");
    } catch (error) {
      console.error("Subscription error:", error);
      setMessage(translations[language].errorMessage);
      setMessageType("error");
    }
  };

  // Get translations for the current language with fallback
  const t = translations[language] || translations.en;

  return (
    <footer className="relative grid grid-cols-1 md:grid-cols-3 justify-items-center items-start gap-11 md:px-44 py-10 text-brown-200">
      {/* Subscription Section */}
      <div className="order-1 md:order-3 flex flex-col items-center w-full space-y-3 md:ml-auto md:my-auto max-w-64">
        <div className="w-full border-b-2 border-brown-500">
          <input
            className="text-brown-700 bg-transparent outline-none text-sm w-full placeholder-brown-300 px-2 py-2"
            type="email"
            placeholder={t.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <button
          onClick={handleSubscribe}
          className="uppercase bg-brown-200 text-brown-700 w-full py-2 text-sm rounded-sm font-semibold hover:bg-brown-300 transition-colors duration-200"
        >
          {t.subscribe}
        </button>
        {message && (
          <p className={`text-xs text-brown-300 mt-2 ${
            messageType === "success" ? "text-brown-300" : "text-red-600"
          }`}>
            {message}
          </p>
        )}
        <p className="max-w-[15rem] text-xs text-brown-300 text-justify">
          {t.newsletterText}
        </p>
      </div>

      {/* Logo */}
      <div className="order-2 md:order-2 flex flex-col items-center m-auto">
        <img
          src="https://i.ibb.co/k8ySvs5/logo3.png"
          alt="Logo"
          className="w-48 md:w-72"
        />
      </div>

      {/* Navigation Links */}
      <div className="order-3 md:order-1 hidden md:flex md:flex-col space-y-10 text-brown-700 mr-auto">
        <FooterLink href="/" label={t.navigation.home} />
        <FooterLink href="/products" label={t.navigation.products} />
        <FooterLink href="/for-chefs" label={t.navigation.forChefs} />
        <FooterLink href="/contact" label={t.navigation.contact} />
        <FooterLink href="/about" label={t.navigation.about} />
      </div>

      {/* Legal Links and Copyright */}
      <div className="order-4 col-span-1 md:col-span-3 text-center md:mt-6">
        <div className="flex justify-center space-x-2 text-xs">
          <FooterLegalLink href="../legacy/terms" label={t.legal.terms} />
          <FooterLegalLink href="../legacy/privacy" label={t.legal.privacy} />
          <FooterLegalLink href="../legacy/cookies" label={t.legal.cookies} />
        </div>
        <p className="text-brown-100 text-xs font-medium mt-1">
          {t.copyright}
        </p>
      </div>
    </footer>
  );
}

function FooterLink({ href, label }: FooterLinkProps) {
  return (
    <Link
      href={href}
      className="font-medium px-2 flex items-center transition-colors duration-200"
    >
      {label}
    </Link>
  );
}

function FooterLegalLink({ href, label }: FooterLinkProps) {
  return (
    <Link
      href={href}
      className="px-2 flex items-center transition-colors duration-200 hover:text-brown-300"
    >
      {label}
    </Link>
  );
}
